import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import deepEqual from 'fast-deep-equal';
import qs from 'qs';

type QueryParams = Record<string, string | string[] | null | undefined>;

interface Props<T> {
  enableQuerySync?: boolean;
  initialState?: T | (() => T);
}

/**
 * string | string[] 처리 시 arrayFormat 기본 값을 repeat로 설정해서
 * string[]의 값이 1개 뿐일 때는 string과 동일한 결과가 나오게 처리
 * @param query
 */
function makeQueryString(query: QueryParams) {
  function alphabeticalSort(a: string, b: string) {
    return a.localeCompare(b);
  }

  return qs.stringify(query, {
    arrayFormat: 'repeat',
    sort: alphabeticalSort,
  });
}

export default function useQueryState<T = Record<string, unknown>>({
  initialState,
  enableQuerySync = false,
}: Props<T>) {
  const router = useRouter();
  const [state, setState] = useState<T | undefined>(() => {
    const initializedValue =
      typeof initialState === 'function' ? (initialState as () => T | undefined)() : initialState;

    if (typeof window === 'undefined' || !enableQuerySync) {
      return initializedValue;
    }

    const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });

    return {
      ...initializedValue,
      ...query,
    } as T;
  });

  // TODO: 추후 query update -> state update 방식으로 개선.
  const updateState = useCallback((partialState: Partial<T>) => {
    setState(prev => {
      const next = !prev ? ({ ...partialState } as T) : { ...prev, ...partialState };

      // 동일한 값이면 setState를 호출하지 않아 무한 루프 방지
      if (deepEqual(prev, next)) {
        return prev;
      }

      return next;
    });
  }, []);

  useEffect(() => {
    if (!router.isReady || !enableQuerySync) {
      return;
    }

    const query = router.query;
    const nextQuery = Object.fromEntries(
      Object.entries({
        ...query,
        ...state,
      }).filter(([_, value]) => Boolean(value)),
    ) as QueryParams;

    const currentQueryString = makeQueryString(query);
    const nextQueryString = makeQueryString(nextQuery);

    if (currentQueryString !== nextQueryString) {
      router.replace(
        {
          query: nextQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [state, enableQuerySync, router]);

  return [state, updateState] as const;
}
